import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  CardContent,
  Stack,
  InputAdornment,
  Tooltip,
  Chip,
  MenuItem,
  Grid,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { ROUTES } from '../../config/routes';
import { getApiErrorMessage } from '../../utils/apiError';
import turnoService, {
  ESTADOS_TURNO,
  EstadoTurno,
  LABEL_ESTADO_TURNO,
  TurnoReadDto,
} from '../../services/turnoService';
import { horaFinTurno } from '../../utils/turnoHorario';

const formatFecha = (value?: string | null): string => {
  if (!value) return '—';
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('es-AR');
};

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' },
};

const btnSx = (bgColor: string, hoverColor: string) => ({
  bgcolor: bgColor,
  color: '#fff',
  borderRadius: 1.5,
  width: 32,
  height: 32,
  '&:hover': { bgcolor: hoverColor, transform: 'scale(1.1)' },
  '&:disabled': { bgcolor: '#ccc' },
  transition: 'all 0.2s',
});

const chipColorEstado = (estado: EstadoTurno): 'default' | 'info' | 'warning' | 'success' => {
  switch (estado) {
    case 'FACTURADO':
      return 'success';
    case 'EN_TRABAJO':
      return 'warning';
    case 'RECIBIDO':
      return 'info';
    default:
      return 'default';
  }
};

const Turnos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [turnos, setTurnos] = useState<TurnoReadDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<'' | EstadoTurno>('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [loading, setLoading] = useState(false);

  const goNuevo = useCallback(() => {
    navigate(ROUTES.servicio.turnos.nuevo);
  }, [navigate]);

  const fetchTurnos = useCallback(
    async (
      pageNum: number,
      pageSize: number,
      search: string,
      estado: '' | EstadoTurno,
      desde: string,
      hasta: string,
    ) => {
      try {
        setLoading(true);
        const data = await turnoService.getTurnos({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search || undefined,
          estado: estado || undefined,
          fechaDesde: desde || undefined,
          fechaHasta: hasta || undefined,
        });
        setTurnos(data.items);
        setTotalCount(data.totalCount);
      } catch (error: unknown) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar turnos'), { variant: 'error' });
        setTurnos([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTurnos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta);
    }, searchTerm ? 300 : 0);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, fetchTurnos]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Insert' && e.key !== 'F2') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) {
        if (e.key === 'F2') return;
      }
      e.preventDefault();
      goNuevo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goNuevo]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleDelete = async (id: number | string) => {
    if (!window.confirm('¿Está seguro de eliminar este turno?')) return;
    try {
      await turnoService.deleteTurno(id);
      enqueueSnackbar('Turno eliminado exitosamente', { variant: 'success' });
      fetchTurnos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta);
    } catch (error: unknown) {
      console.error('Error al eliminar turno:', error);
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al eliminar turno';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleEdit = (turno: TurnoReadDto) => {
    const id = String(turno.id ?? '').trim();
    if (id) navigate(`/servicio/turnos/${encodeURIComponent(id)}`);
  };

  const clienteLabel = (t: TurnoReadDto) => {
    const nombre = (t.nombreCliente || '').trim();
    if (nombre) return nombre;
    if (t.nroCliente) return String(t.nroCliente);
    return '—';
  };

  const servicioLabel = (t: TurnoReadDto) =>
    (t.descripcionServicio || t.codigoServicio || '—').trim() || '—';

  const horarioInicioFin = (t: TurnoReadDto): string => {
    const inicio = (t.horaInicio || '').trim();
    if (!inicio) return '—';
    const fin = horaFinTurno(inicio, Number(t.duracionMinutos) || 0);
    return `${inicio} - ${fin}`;
  };

  const renderEstado = (t: TurnoReadDto) => {
    // Si está anulado, solo se muestra ese estado (no se combina con PROGRAMADO/etc.).
    if (t.anulado) {
      return <Chip size="small" label="Anulado" color="error" />;
    }
    return (
      <Chip
        size="small"
        label={LABEL_ESTADO_TURNO[t.estado] || t.estado}
        color={chipColorEstado(t.estado)}
      />
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Turnos
            </Typography>
            <Tooltip title="Insert o F2">
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={goNuevo}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': { boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)' },
                }}
              >
                Agregar turno
              </Button>
            </Tooltip>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  placeholder="Buscar por cliente, servicio o nota..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Estado"
                  value={filtroEstado}
                  onChange={(e) => {
                    setFiltroEstado(e.target.value as '' | EstadoTurno);
                    setPage(0);
                  }}
                  sx={fieldSx}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {ESTADOS_TURNO.map((e) => (
                    <MenuItem key={e} value={e}>
                      {LABEL_ESTADO_TURNO[e]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  fullWidth
                  label="Fecha desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  fullWidth
                  label="Fecha hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={fechaDesde ? { min: fechaDesde } : undefined}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          </Box>

          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <Typography color="text.secondary">Cargando...</Typography>
            </Box>
          ) : isMobile ? (
            <Stack spacing={2}>
              {turnos.length === 0 ? (
                <Typography color="text.secondary" align="center">
                  No hay turnos para mostrar
                </Typography>
              ) : (
                turnos.map((t) => {
                  const id = String(t.id ?? '');
                  return (
                    <Card key={id || `${t.fecha}-${t.horaInicio}-${clienteLabel(t)}`} variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {formatFecha(t.fecha)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {horarioInicioFin(t)}
                          </Typography>
                        </Box>
                        <Typography variant="body2">{clienteLabel(t)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {servicioLabel(t)}
                        </Typography>
                        {renderEstado(t)}
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(t)}
                            disabled={!id}
                            sx={btnSx('#2196f3', '#1976d2')}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(t.id)}
                            disabled={!id}
                            sx={btnSx('#f44336', '#d32f2f')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Card>
                  );
                })
              )}
            </Stack>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, width: 140, whiteSpace: 'nowrap' }}>Fecha / Horario</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '22%' }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '26%' }}>Servicio</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 160 }}>Estado</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: 96, whiteSpace: 'nowrap' }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {turnos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          No hay turnos para mostrar
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    turnos.map((t) => {
                      const id = String(t.id ?? '');
                      return (
                        <TableRow
                          key={id || `${t.fecha}-${t.horaInicio}-${clienteLabel(t)}`}
                          sx={{
                            '&:hover': {
                              bgcolor: '#f9f9f9',
                            },
                          }}
                        >
                          <TableCell sx={{ verticalAlign: 'middle' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                              {formatFecha(t.fecha)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                              {horarioInicioFin(t)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={clienteLabel(t)}
                          >
                            {clienteLabel(t)}
                          </TableCell>
                          <TableCell
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={servicioLabel(t)}
                          >
                            {servicioLabel(t)}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>{renderEstado(t)}</TableCell>
                          <TableCell align="center" sx={{ whiteSpace: 'nowrap', width: 96 }}>
                            <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="nowrap">
                              <Tooltip title="Editar">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEdit(t)}
                                    disabled={!id}
                                    sx={btnSx('#2196f3', '#1976d2')}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Eliminar">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(t.id)}
                                    disabled={!id}
                                    sx={btnSx('#f44336', '#d32f2f')}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Turnos;
